// ─── DATABASE CLIENT ─────────────────────────────────────────────────
import { API_BASE } from './config';
import { authHeaders, apiPost, ensureFreshToken, clearExpiredSession } from './auth';
import { mergeSeedDocTemplates } from './seed-doc-templates';

let reloadTimer = null;
/** Debounced full refresh after writes — coalesces burst POST/PUT into one loadAll. */
export function notifyDataChanged() {
  if (typeof window === 'undefined') return;
  clearTimeout(reloadTimer);
  reloadTimer = setTimeout(() => window.dispatchEvent(new Event('fb-reload')), 50);
}

function skipReloadOnUpdate(table, matchKey, data) {
  return table === 'notifications' && matchKey === 'id' && data?.read === true;
}

const REST_LIST = {
  companies: '/clients',
  invoices: '/invoices',
  payments: '/payments',
  tickets: '/tickets',
  submissions: '/submissions',
  users: '/users',
  cross_sell_opportunities: '/cross-sell-opportunities',
};

const REST_WRITE = {
  companies: '/clients',
  invoices: '/invoices',
  payments: '/payments',
  tickets: '/tickets',
  submissions: '/submissions',
  notifications: '/notifications',
  users: '/users',
  cross_sell_opportunities: '/cross-sell-opportunities',
};

async function apiRequest(method, path, body) {
  await ensureFreshToken();
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: authHeaders(),
    ...(body != null ? { body: JSON.stringify(body) } : {}),
  });
  const json = await res.json().catch(() => ({}));
  if (res.status === 401) {
    clearExpiredSession();
    throw new Error(json.error || 'Invalid or expired session');
  }
  if (!res.ok || json.success === false) throw new Error(json.error || `HTTP ${res.status}`);
  return json;
}

function asArray(data) {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') return [data];
  return [];
}

async function restGetList(base, filter = '', { limit, cursor } = {}) {
  if (filter.includes('id=eq.')) {
    const id = filter.split('id=eq.')[1]?.split('&')[0];
    const json = await apiRequest('GET', `${base}/${id}`);
    return asArray(json.data);
  }
  const params = new URLSearchParams();
  if (filter) params.set('filter', filter);
  if (limit) params.set('limit', String(limit));
  if (cursor) params.set('cursor', cursor);
  const q = params.toString() ? `?${params.toString()}` : '';
  const json = await apiRequest('GET', `${base}${q}`);
  return asArray(json.data);
}

function restListBase(table, filter = '') {
  if (table === 'tasks') {
    const recurringOnly =
      filter.includes('is_recurring=eq.true') &&
      !filter.includes('is_recurring=eq.false');
    return recurringOnly ? '/tasks/recurring' : '/tasks';
  }
  return REST_LIST[table] || null;
}

export const sb = {
  _hasCreatedAt: ["users", "companies", "invoices", "tasks", "submissions", "payments", "tickets", "ticket_responses", "notifications", "bundles", "documents"],
  _hasUpdatedAt: ["tasks", "tickets"],

  async select(table, filter = "") {
    try {
      if (table === "tasks") {
        let path;
        if (filter.includes("id=eq.")) {
          const id = filter.split("id=eq.")[1]?.split("&")[0];
          path = `/tasks/${id}`;
          const json = await apiRequest('GET', path);
          return asArray(json.data);
        }
        path = restListBase(table, filter);
        const q = filter ? `?filter=${encodeURIComponent(filter)}` : '';
        const json = await apiRequest('GET', `${path}${q}`);
        return asArray(json.data);
      }
      if (table === "notifications" && filter.includes("user_id=eq.")) {
        const uid = filter.split("user_id=eq.")[1]?.split("&")[0];
        const json = await apiRequest('GET', `/notifications/${uid}`);
        return asArray(json.data);
      }
      if (table === "payment_schedule") {
        if (filter.includes("invoice_id=eq.")) {
          const invId = filter.split("invoice_id=eq.")[1]?.split("&")[0];
          const json = await apiRequest('GET', `/payments/schedule/${invId}`);
          return asArray(json.data);
        }
        const q = filter ? `?filter=${encodeURIComponent(filter)}` : '';
        const json = await apiRequest('GET', `/payments/schedule${q}`);
        return asArray(json.data);
      }
      if (table === "invoice_line_items" && filter.includes("invoice_id=eq.")) {
        const invId = filter.split("invoice_id=eq.")[1]?.split("&")[0];
        const json = await apiRequest('GET', `/invoices/${invId}/line-items`);
        return asArray(json.data);
      }
      const listBase = REST_LIST[table];
      if (listBase) {
        const pageLimit = filter?.includes('id=eq.') ? undefined : 200;
        return await restGetList(listBase, filter, { limit: pageLimit });
      }
      const rows = await apiPost("/db/select", { table, filter });
      return Array.isArray(rows) ? rows : [];
    } catch (e) {
      console.error(`❌ Select [${table}] error:`, e.message);
      return [];
    }
  },

  /** Cursor-paginated list — returns { items, nextCursor, hasMore }. */
  async selectPage(table, filter = "", { limit = 50, cursor = null } = {}) {
    try {
      const pageLimit = Math.min(Math.max(limit || 50, 1), 200);
      const base = restListBase(table, filter);
      if (!base) {
        const rows = await apiPost("/db/select", { table, filter });
        const arr = Array.isArray(rows) ? rows : [];
        return { items: arr.slice(0, pageLimit), nextCursor: null, hasMore: false };
      }
      const params = new URLSearchParams();
      if (filter) params.set('filter', filter);
      params.set('limit', String(pageLimit));
      if (cursor) params.set('cursor', cursor);
      const json = await apiRequest('GET', `${base}?${params.toString()}`);
      const pagination = json.pagination || {};
      return {
        items: asArray(json.data),
        nextCursor: pagination.nextCursor || null,
        hasMore: !!pagination.hasMore,
      };
    } catch (e) {
      console.error(`❌ selectPage [${table}]:`, e.message);
      return { items: [], nextCursor: null, hasMore: false };
    }
  },

  async insert(table, data) {
    try {
      let out;
      if (table === "tasks") {
        const json = await apiRequest('POST', '/tasks', data);
        out = asArray(json.data);
      } else if (REST_WRITE[table]) {
        const json = await apiRequest('POST', REST_WRITE[table], data);
        out = asArray(json.data);
      } else if (table === "payment_schedule") {
        const json = await apiRequest('POST', '/payments/schedule', data);
        out = asArray(json.data);
      } else {
        const rows = await apiPost("/db/insert", { table, data });
        out = Array.isArray(rows) ? rows : [rows];
      }
      notifyDataChanged();
      return out;
    } catch (e) {
      console.error(`❌ Insert [${table}] error:`, e.message);
      throw e;
    }
  },

  async update(table, matchKey, matchVal, data) {
    try {
      let out;
      if (table === "tasks" && matchKey === "id") {
        const json = await apiRequest('PUT', `/tasks/${matchVal}`, data);
        out = asArray(json.data);
      } else if (table === "submissions" && matchKey === "id" && data.status !== undefined) {
        const json = await apiRequest('PUT', `/submissions/${matchVal}/review`, {
          status: data.status,
          review_note: data.review_note || data.reviewNote || '',
          rejected_items: data.rejected_items ?? data.rejectedItems ?? [],
        });
        out = asArray(json.data);
      } else if (table === "notifications" && matchKey === "id" && data.read === true) {
        const json = await apiRequest('PUT', `/notifications/${matchVal}/read`, {});
        return asArray(json.data);
      } else if (table === "payment_schedule" && matchKey === "id") {
        const json = await apiRequest('PUT', `/payments/schedule/${matchVal}`, data);
        out = asArray(json.data);
      } else if (matchKey === "id" && REST_WRITE[table]) {
        const json = await apiRequest('PUT', `${REST_WRITE[table]}/${matchVal}`, data);
        out = asArray(json.data);
      } else {
        const rows = await apiPost("/db/update", { table, matchKey, matchVal, data });
        out = Array.isArray(rows) ? rows : [rows];
      }
      if (!skipReloadOnUpdate(table, matchKey, data)) notifyDataChanged();
      return out;
    } catch (e) {
      console.error(`❌ Update [${table}] error:`, e.message);
      return [];
    }
  },

  async upsert(table, data) {
    return this.insert(table, data);
  },

  async delete(table, filter) {
    try {
      let out;
      if (table === "tasks" && filter.includes("id=eq.")) {
        const id = filter.split("id=eq.")[1]?.split("&")[0];
        const json = await apiRequest('DELETE', `/tasks/${id}`);
        out = asArray(json.data);
      } else if (table === "payment_schedule" && filter.includes("id=eq.")) {
        const id = filter.split("id=eq.")[1]?.split("&")[0];
        const json = await apiRequest('DELETE', `/payments/schedule/${id}`);
        out = asArray(json.data);
      } else if (table === "cross_sell_opportunities" && filter.includes("id=eq.")) {
        const id = filter.split("id=eq.")[1]?.split("&")[0];
        const json = await apiRequest('DELETE', `/cross-sell-opportunities/${id}`);
        out = asArray(json.data);
      } else {
        out = await apiPost("/db/delete", { table, filter });
      }
      notifyDataChanged();
      return out;
    } catch (e) {
      console.error(`❌ Delete [${table}] error:`, e.message);
      return [];
    }
  },
  // Create an invoice via the dedicated API. The server also persists the line
  // items, auto-creates tasks from the line-item templates, and builds any
  // payment schedule — returns { ...invoice, line_items, tasks }.
  async createInvoice(payload) {
    const res = await fetch(`${API_BASE}/invoices`, {
      method: "POST", headers: authHeaders(), body: JSON.stringify(payload),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json.success === false) throw new Error(json.error || `HTTP ${res.status}`);
    notifyDataChanged();
    return json.data || {};
  },

  async updateInvoice(id, payload) {
    const res = await fetch(`${API_BASE}/invoices/${id}`, {
      method: "PUT", headers: authHeaders(), body: JSON.stringify(payload),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json.success === false) throw new Error(json.error || `HTTP ${res.status}`);
    notifyDataChanged();
    return json.data || {};
  },

  // ── Bundles (dedicated REST) ──────────────────────────────────────
  async listBundles() {
    const res = await fetch(`${API_BASE}/bundles`, { headers: authHeaders() });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json.success === false) throw new Error(json.error || `HTTP ${res.status}`);
    return Array.isArray(json.data) ? json.data : [];
  },

  async getBundle(id) {
    const res = await fetch(`${API_BASE}/bundles/${id}`, { headers: authHeaders() });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json.success === false) throw new Error(json.error || `HTTP ${res.status}`);
    return json.data || null;
  },

  async createBundle(payload) {
    const res = await fetch(`${API_BASE}/bundles`, {
      method: "POST", headers: authHeaders(), body: JSON.stringify(payload),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json.success === false) throw new Error(json.error || `HTTP ${res.status}`);
    notifyDataChanged();
    return json.data || {};
  },

  async updateBundle(id, payload) {
    const res = await fetch(`${API_BASE}/bundles/${id}`, {
      method: "PUT", headers: authHeaders(), body: JSON.stringify(payload),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json.success === false) throw new Error(json.error || `HTTP ${res.status}`);
    notifyDataChanged();
    return json.data || {};
  },

  async deleteBundle(id) {
    const res = await fetch(`${API_BASE}/bundles/${id}`, {
      method: "DELETE", headers: authHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json.success === false) throw new Error(json.error || `HTTP ${res.status}`);
    notifyDataChanged();
    return json.data || {};
  },

  // ── Document templates (dedicated REST) ───────────────────────────
  // Returns a map keyed by id (matches UI state). API list is an array.
  async listDocTemplates() {
    const res = await fetch(`${API_BASE}/document-templates`, { headers: authHeaders() });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json.success === false) throw new Error(json.error || `HTTP ${res.status}`);
    const list = Array.isArray(json.data) ? json.data : [];
    const map = {};
    for (const t of list) { if (t && t.id) map[t.id] = t; }
    return mergeSeedDocTemplates(map);
  },

  async getDocTemplate(id) {
    const res = await fetch(`${API_BASE}/document-templates/${id}`, { headers: authHeaders() });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json.success === false) throw new Error(json.error || `HTTP ${res.status}`);
    return json.data || null;
  },

  async createDocTemplate(payload) {
    const res = await fetch(`${API_BASE}/document-templates`, {
      method: "POST", headers: authHeaders(), body: JSON.stringify(payload),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json.success === false) throw new Error(json.error || `HTTP ${res.status}`);
    notifyDataChanged();
    return json.data || {};
  },

  async updateDocTemplate(id, payload) {
    const res = await fetch(`${API_BASE}/document-templates/${id}`, {
      method: "PUT", headers: authHeaders(), body: JSON.stringify(payload),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json.success === false) throw new Error(json.error || `HTTP ${res.status}`);
    notifyDataChanged();
    return json.data || {};
  },

  async deleteDocTemplate(id) {
    const res = await fetch(`${API_BASE}/document-templates/${id}`, {
      method: "DELETE", headers: authHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json.success === false) throw new Error(json.error || `HTTP ${res.status}`);
    return json.data || {};
  },

  // Replace entire catalog (object map or array) — used by Doc Templates settings
  async saveDocTemplates(templates) {
    const res = await fetch(`${API_BASE}/document-templates`, {
      method: "PUT", headers: authHeaders(), body: JSON.stringify({ templates }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json.success === false) throw new Error(json.error || `HTTP ${res.status}`);
    const list = Array.isArray(json.data) ? json.data : [];
    const map = {};
    for (const t of list) { if (t && t.id) map[t.id] = t; }
    notifyDataChanged();
    return map;
  },

  // ── Users & roles (dedicated REST) ────────────────────────────────
  async listUsers(filter = '') {
    const q = filter ? `?filter=${encodeURIComponent(filter)}` : '';
    const json = await apiRequest('GET', `/users${q}`);
    return asArray(json.data);
  },

  async createUser(payload) {
    const json = await apiRequest('POST', '/users', payload);
    notifyDataChanged();
    return asArray(json.data);
  },

  async updateUser(id, payload) {
    const json = await apiRequest('PUT', `/users/${id}`, payload);
    notifyDataChanged();
    return asArray(json.data);
  },

  async listEmployees() {
    const json = await apiRequest('GET', '/employees');
    return asArray(json.data);
  },

  async listRoles() {
    const res = await fetch(`${API_BASE}/roles`, { headers: authHeaders() });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json.success === false) throw new Error(json.error || `HTTP ${res.status}`);
    return Array.isArray(json.data) ? json.data : [];
  },

  async createRole(payload) {
    const res = await fetch(`${API_BASE}/roles`, {
      method: "POST", headers: authHeaders(), body: JSON.stringify(payload),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json.success === false) throw new Error(json.error || `HTTP ${res.status}`);
    notifyDataChanged();
    return json.data || {};
  },

  async updateRole(id, payload) {
    const res = await fetch(`${API_BASE}/roles/${id}`, {
      method: "PUT", headers: authHeaders(), body: JSON.stringify(payload),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json.success === false) throw new Error(json.error || `HTTP ${res.status}`);
    notifyDataChanged();
    return json.data || {};
  },

  async deleteRole(id) {
    const res = await fetch(`${API_BASE}/roles/${id}`, {
      method: "DELETE", headers: authHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json.success === false) throw new Error(json.error || `HTTP ${res.status}`);
    notifyDataChanged();
    return json.data || {};
  },

  // ── Organisation settings ───────────────────────────────────────────
  async getOrganisationSettings() {
    const res = await fetch(`${API_BASE}/organisation/settings`, { headers: authHeaders() });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json.success === false) throw new Error(json.error || `HTTP ${res.status}`);
    return json.data || {};
  },

  async createOrganisationSettings(payload) {
    const res = await fetch(`${API_BASE}/organisation/settings`, {
      method: "POST", headers: authHeaders(), body: JSON.stringify(payload),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json.success === false) throw new Error(json.error || `HTTP ${res.status}`);
    notifyDataChanged();
    return json.data || {};
  },

  async updateOrganisationSettings(payload) {
    const json = await apiRequest('PUT', '/organisation/settings', payload);
    notifyDataChanged();
    return json.data || {};
  },

  async getOrganisationKraya() {
    const json = await apiRequest('GET', '/organisation/kraya');
    return json.data || {};
  },

  async updateOrganisationKraya(payload) {
    const json = await apiRequest('PUT', '/organisation/kraya', payload);
    notifyDataChanged();
    return json.data || {};
  },

  async getOrganisationZohoWorkdrive() {
    const json = await apiRequest('GET', '/organisation/zoho-workdrive');
    return json.data || {};
  },

  async updateOrganisationZohoWorkdrive(payload) {
    const json = await apiRequest('PUT', '/organisation/zoho-workdrive', payload);
    notifyDataChanged();
    return json.data || {};
  },

  async markAllNotificationsRead(userId) {
    const json = await apiRequest('PUT', `/notifications/read-all/${userId}`, {});
    return json.data || {};
  },

  // ── Delete Requests (admin approval workflow) ───────────────────────
  async createDeleteRequest(payload) {
    const json = await apiRequest('POST', '/delete-requests', payload);
    notifyDataChanged();
    return json.data || {};
  },

  async listDeleteRequests(filter = '') {
    const q = filter ? `?filter=${encodeURIComponent(filter)}` : '';
    const json = await apiRequest('GET', `/delete-requests${q}`);
    return Array.isArray(json.data) ? json.data : [];
  },

  async reviewDeleteRequest(id, review) {
    const json = await apiRequest('PUT', `/delete-requests/${id}/review`, review);
    notifyDataChanged();
    return json.data || {};
  },
};
